import React, {useEffect, createContext, useContext, useState, useMemo} from "react";
import {useLocalStorage} from "@unibuddy/patron";
import {ApolloError} from "@apollo/client";
import {useErrorReporting} from "@unibuddy/error-reporting";

import {
    AssistantConversationMessage,
    SenderRole,
    useCreateAssistantConversationMutation,
    useGetAssistantConversationQuery,
} from "../../../types/generated";
import {useUniversityContext} from "../../../providers/UniversityProvider";
import {AssistantError} from "../components/AssistantError/AssistantError";
import {getErrorString} from "../../../utils/getErrorString";
import {UseLocalStorageReturn} from "../types";

export const ASSISTANT_CONVERSATION_ID_KEY = "ASSISTANT_CONVERSATION_ID_KEY";
export const ASSISTANT_CONVERSATION_TOKEN_KEY = "ASSISTANT_CONVERSATION_TOKEN_KEY";
const ASSISTANT_CONVERSATION_STARTED = "ASSISTANT_CONVERSATION_STARTED";

export type AssistantInitiationContextType = {
    loading: boolean;
    error?: ApolloError;
    conversationId?: string;
    messages: AssistantConversationMessage[];
    userHasSentMessage: boolean | null;
    feedback: {rating: number} | null;
    leadGenerated: boolean | null;
    token?: string;
};

const AssistantInitiationContext = createContext<AssistantInitiationContextType>({
    loading: true,
    error: undefined,
    conversationId: undefined,
    messages: [],
    userHasSentMessage: null,
    feedback: null,
    leadGenerated: null,
    token: undefined,
});

export function useAssistantInitiationContext() {
    return useContext(AssistantInitiationContext);
}

type AssistantInitiationProviderProps = {
    children: React.ReactNode;
};

export const AssistantInitiationProvider = ({children}: AssistantInitiationProviderProps) => {
    const {reportError} = useErrorReporting();
    const {universityId, widgetUrl} = useUniversityContext();
    const [userHasSentMessage, setUserHasSentMessage] = useState<boolean | null>(null);
    const [id, setId] = useLocalStorage(
        `${ASSISTANT_CONVERSATION_ID_KEY}-${universityId}`,
        "",
    ) as UseLocalStorageReturn<string>;
    const [token, setToken] = useLocalStorage(
        `${ASSISTANT_CONVERSATION_TOKEN_KEY}-${universityId}`,
        "",
    ) as UseLocalStorageReturn<string>;
    const [userHasOpenedAssistant, setUserHasOpenedAssistant] = useLocalStorage(
        `${ASSISTANT_CONVERSATION_STARTED}-${universityId}`,
        false,
    ) as UseLocalStorageReturn<boolean>;

    const {data, loading, error} = useGetAssistantConversationQuery({
        variables: {
            id,
            universityId,
            token: !token || token === "" ? undefined : token,
        },
        skip: !id,
    });
    const [createAssistantConversation, {loading: createLoading, error: createError}] =
        useCreateAssistantConversationMutation();

    const value = useMemo(
        () => ({
            messages: data?.getAssistantConversation?.messages ?? [],
            loading: createLoading || loading,
            error,
            conversationId: id,
            userHasSentMessage,
            feedback: data?.getAssistantConversation?.feedback ?? null,
            leadGenerated: data?.getAssistantConversation?.leadGenerated ?? null,
            token: !token || token === "" ? undefined : token,
        }),
        [
            createLoading,
            loading,
            id,
            token,
            error,
            userHasSentMessage,
            data?.getAssistantConversation?.messages,
            data?.getAssistantConversation?.feedback,
            data?.getAssistantConversation?.leadGenerated,
        ],
    );

    useEffect(() => {
        const messageListener = (e: MessageEvent) => {
            if (e.data === "chatVisible=true") {
                setUserHasOpenedAssistant(true);
            }
        };

        window.addEventListener("message", messageListener, true);

        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, [setUserHasOpenedAssistant]);

    useEffect(() => {
        if (data && !data.getAssistantConversation) {
            setId("");
        }
    }, [data, setId]);

    useEffect(() => {
        const createConversation = async () => {
            if (!createLoading && universityId) {
                try {
                    const result = await createAssistantConversation({
                        variables: {universityId},
                    });
                    if (result.data?.createAssistantConversation.id) {
                        const token = result.data?.createAssistantConversation.token ?? "";

                        setId(result.data.createAssistantConversation.id);
                        setToken(token);
                    } else {
                        throw new Error("Could not create conversation. Try again later.");
                    }
                } catch (error) {
                    console.error(error);
                    reportError(
                        new Error(`Error while creating conversation: ${getErrorString(error)}`),
                    );
                }
            }
        };
        if (window.self === window.top) {
            setUserHasOpenedAssistant(true);
        }

        if (id === "" && !createError && userHasOpenedAssistant) {
            void createConversation();
        }
    }, [
        createAssistantConversation,
        createError,
        createLoading,
        id,
        setId,
        universityId,
        userHasOpenedAssistant,
        setUserHasOpenedAssistant,
        reportError,
        setToken,
    ]);

    useEffect(() => {
        if (!userHasSentMessage) {
            if (data?.getAssistantConversation?.messages) {
                for (let i = 0; i < data.getAssistantConversation.messages.length; i++) {
                    if (data.getAssistantConversation.messages[i].sender !== SenderRole.Assistant) {
                        setUserHasSentMessage(true);
                        break;
                    }
                    if (i === data.getAssistantConversation.messages.length - 1) {
                        setUserHasSentMessage(false);
                    }
                }
            }
        }
    }, [data?.getAssistantConversation?.messages, userHasSentMessage]);

    if (error || createError) {
        return <AssistantError widgetUrl={widgetUrl} />;
    }

    return (
        <AssistantInitiationContext.Provider value={value}>
            {children}
        </AssistantInitiationContext.Provider>
    );
};
